import {
  isBefore,
  isAfter,
  isEqual,
  differenceInYears,
  differenceInHours,
  eachMonthOfInterval
} from 'date-fns';

import {
  getDateAsUTC,
  formatDate,
  formatReadableDate,
  MONTH_NUMBER_DATE_FORMAT,
  formatMonthlyPaymentDate
} from './dateUtils';

const MONTH_DICTIONARY = {
  1: 'jan',
  2: 'feb',
  3: 'mar',
  4: 'apr',
  5: 'may',
  6: 'jun',
  7: 'jul',
  8: 'aug',
  9: 'sep',
  10: 'oct',
  11: 'nov',
  12: 'dec'
};

// Returns ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12']
export const getMonths = (from, to, dateFormat = 'MM') => {
  const startDate = getDateAsUTC(
    typeof from === 'string' ? new Date(from) : new Date()
  );

  const endDate = getDateAsUTC(
    typeof to === 'string' ? new Date(to) : new Date()
  );

  const months = eachMonthOfInterval({
    start: startDate,
    end: endDate
  }).map(month => formatDate(month, dateFormat));

  return months;
};

// Returns ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
export const getMonthsStaying = (moveIn, moveOut) => {
  if (!moveIn || !moveOut) {
    return [];
  }

  const months = getMonths(moveIn, moveOut, MONTH_NUMBER_DATE_FORMAT).map(
    month => MONTH_DICTIONARY[month]
  );

  return months;
};

export const getUserAge = (birthDate, today = new Date()) =>
  differenceInYears(getDateAsUTC(today), getDateAsUTC(birthDate));

export const getDaysDiff = (start, end) => {
  return (
    Math.round(
      differenceInHours(
        getDateAsUTC(new Date(end)),
        getDateAsUTC(new Date(start))
      ) / 24
    ) || 0 // to avoid issues with hour change
  );
};

export const dateIsBetween = (date, initialRangeDate, endRangeDate) => {
  const formatDateUTC = getDateAsUTC(date);
  const initialRangeDateUTC = getDateAsUTC(initialRangeDate);
  const endRangeDateUTC = getDateAsUTC(endRangeDate);
  return (
    (isAfter(formatDateUTC, initialRangeDateUTC) &&
      isBefore(formatDateUTC, endRangeDateUTC)) ||
    isEqual(formatDateUTC, initialRangeDateUTC) ||
    isEqual(formatDateUTC, endRangeDateUTC)
  );
};

export const checkIfAvailable = (moveIn, moveOut, unavailabilities) => {
  const startDate = getDateAsUTC(moveIn);
  const endDate = getDateAsUTC(moveOut);

  return !unavailabilities.some(u => {
    const fromU = getDateAsUTC(u.from);
    const toU = getDateAsUTC(u.to);

    return (
      dateIsBetween(startDate, u.from, u.to) ||
      dateIsBetween(endDate, u.from, u.to) ||
      dateIsBetween(fromU, startDate, endDate) ||
      dateIsBetween(toU, startDate, endDate)
    );
  });
};

export const formatReadableDateRange = (
  moveIn,
  moveOut,
  useMonthFormat = false
) => {
  if (!moveIn && !moveOut) {
    return null;
  }
  const formatDates = useMonthFormat
    ? formatMonthlyPaymentDate
    : formatReadableDate;

  const dates = [];
  if (moveIn) {
    dates.push(formatDates(moveIn));
  }

  if (moveOut) {
    dates.push(formatDates(moveOut));
  }

  return dates.join(' - ');
};

export const dateIsEqualOrAfter = (date, dateToCompare) =>
  isAfter(getDateAsUTC(date), getDateAsUTC(dateToCompare)) ||
  isEqual(getDateAsUTC(date), getDateAsUTC(dateToCompare));

export const dateIsEqualOrBefore = (date, dateToCompare) =>
  isBefore(getDateAsUTC(date), getDateAsUTC(dateToCompare)) ||
  isEqual(getDateAsUTC(date), getDateAsUTC(dateToCompare));

export const dateIsBefore = (date, dateToCompare) =>
  isBefore(getDateAsUTC(date), getDateAsUTC(dateToCompare));
